import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, BookOpen, PlayCircle, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { PlaybookPanel } from '@/components/routines/PlaybookPanel';
import { RoutineTierBadge, tierLabel, tierMeaning } from '@/components/routines/tier';
import {
  BORDMITTEL_LABEL,
  BORDMITTEL_MEANING,
  RoutineCollectionsBadge,
  collectionsMeaning,
  normalizeCollections,
} from '@/components/routines/collections';
import { executionOrderOf, useRoutine } from '@/hooks/useRoutines';
import { formatDateTime } from '@/lib/format';

/**
 * /routines/$slug - Routinen-Detail (Sprint 5; Playbook-Editor Sprint 8).
 *
 * Header mit Metadaten + Playbook-Panel (CodeMirror, lazy-Chunk): YAML mit
 * Highlighting/Zeilennummern, Web-Editor mit Validierung und Git-Commit
 * beim Speichern.
 */

export const Route = createFileRoute('/routines/$slug')({
  component: RoutineDetailPage,
});

function RoutineDetailPage(): JSX.Element {
  const { slug } = useParams({ from: '/routines/$slug' });
  const query = useRoutine(slug);

  return (
    <div className="flex flex-col gap-4">
      <Link to="/routines" className="inline-flex items-center gap-1 text-sm text-muted hover:text-text">
        <ArrowLeft className="size-3" /> Zur Library
      </Link>

      {query.isLoading && <SkeletonRows rows={3} cols={2} />}
      {query.isError && <ErrorAlert error={query.error} />}

      {query.data && (
        <>
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <BookOpen className="size-5 text-primary" aria-hidden="true" />
                <h1 className="text-2xl font-semibold">{query.data.display_name}</h1>
                {/* Kein Badge bei "produktiv": der Normalfall braucht keine
                    Marke. Die Stufe steht ausgeschrieben in den Metadaten. */}
                <RoutineTierBadge
                  tier={query.data.tier}
                  data-testid="routine-detail-tier-badge"
                />
                {/* Auch hier kein Badge im Normalfall: "nur Bordmittel"
                    braucht keine Marke, eine Galaxy-Abhängigkeit schon -
                    sie ist eine Voraussetzung am Wartungs-PC. */}
                <RoutineCollectionsBadge
                  collections={query.data.required_collections}
                  data-testid="routine-detail-collections-badge"
                />
                {query.data.requires_snapshot && (
                  <Badge tone="primary">
                    <span className="inline-flex items-center gap-1">
                      <ShieldAlert className="size-3" aria-hidden="true" /> Snapshot Pflicht
                    </span>
                  </Badge>
                )}
                {query.data.is_destructive && <Badge tone="danger">destruktiv</Badge>}
              </div>
              <p className="mt-1 text-sm text-muted">
                <code className="font-mono">{query.data.code}</code>
              </p>
              {query.data.description && (
                <p className="mt-2 max-w-2xl text-sm">{query.data.description}</p>
              )}
            </div>
            <Link to="/runs/new" search={{ routine: query.data.code }}>
              <Button leftIcon={<PlayCircle className="size-4" />}>
                Diese Routine starten
              </Button>
            </Link>
          </header>

          <Card title="Metadaten" description="Capabilities und Source der Routine">
            <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2 lg:grid-cols-3">
              <DefRow
                label="Reihenfolge"
                value={
                  <span data-testid="routine-detail-order">
                    {executionOrderOf(query.data)}
                  </span>
                }
                mono
              />
              <DefRow
                label="Reifegrad"
                value={
                  <span
                    title={tierMeaning(query.data.tier)}
                    data-testid="routine-detail-tier"
                  >
                    {tierLabel(query.data.tier)}
                  </span>
                }
              />
              <DefRow
                label="Herkunft der Module"
                value={<CollectionsValue routine={query.data} />}
              />
              <DefRow
                label="Snapshot Pflicht?"
                value={query.data.requires_snapshot ? 'Ja (Pflicht)' : 'Optional'}
              />
              <DefRow
                label="Destruktiv?"
                value={query.data.is_destructive ? 'Ja' : 'Nein'}
              />
              <DefRow
                label="Geschätzte Dauer"
                value={
                  query.data.estimated_duration_seconds != null
                    ? `${query.data.estimated_duration_seconds}s`
                    : '–'
                }
                mono
              />
              <DefRow
                label="Playbook-Pfad"
                value={query.data.ansible_playbook_path ?? '–'}
                mono
              />
              <DefRow label="Erstellt" value={formatDateTime(query.data.created_at)} />
              <DefRow label="Aktualisiert" value={formatDateTime(query.data.updated_at)} />
            </dl>
            {/* Ohne diesen Satz sucht man das Feld im UI und findet es nie:
                die Registry liest den Wert bei jedem Backend-Start aus dem
                Playbook und überschreibt alles, was in der DB steht. */}
            <p className="mt-4 text-sm text-muted" data-testid="routine-order-hint">
              <strong>Reihenfolge</strong> steuert, welche Routine bei &laquo;Alles
              warten&raquo; zuerst laeuft &mdash; kleinere Zahl frueher (Default 100).
              Geaendert wird sie im Meta-Header des Playbooks unten auf dieser Seite
              (&laquo;Bearbeiten&raquo;), Zeile{' '}
              <code className="font-mono text-xs">execution_order: 100</code>. Ein Wert in
              der Datenbank hätte keinen Bestand: der nächste Backend-Start liest den
              Header erneut ein.
            </p>
            {/* Bis 13.08.2026 stand hier bei jeder Routine "experimental" - der
                Upsert schrieb das Wort als Konstante. Wer die Stufe wieder
                aussagekräftig halten will, muss wissen, was sie behauptet. */}
            <p className="mt-3 text-sm text-muted" data-testid="routine-tier-hint">
              <strong>Reifegrad</strong> sagt, wie viel Erprobung BELEGT ist, nicht wie gut
              die Routine gemeint ist: <em>produktiv</em> = mindestens ein erfolgreicher
              Durchlauf auf einem echten System belegt, <em>beta</em> = laeuft an, aber kein
              sauberer Durchlauf belegt, <em>experimentell</em> = noch kein einziger Lauf
              belegt, <em>veraltet</em> = abgeloest. Auch dieser Wert steht im Meta-Header
              des Playbooks, Zeile{' '}
              <code className="font-mono text-xs">tier: production</code>; ohne Angabe gilt{' '}
              <code className="font-mono text-xs">beta</code>. Hochstufen bitte erst, wenn
              der Lauf wirklich sauber durch war.
            </p>
            {/* Anlass 26.08.2026: routinen/requirements.yml behauptete, alle
                Playbooks kämen mit ansible.builtin aus - die beiden
                Windows-Routinen benutzten da längst ansible.windows. Ohne
                die Collection stirbt der Lauf am ersten Task, und das sieht
                aus wie eine kaputte Routine. */}
            <p className="mt-3 text-sm text-muted" data-testid="routine-collections-hint">
              <strong>Herkunft der Module</strong> sagt, ob die Routine mit{' '}
              <code className="font-mono text-xs">ansible-core</code> auskommt
              (&laquo;nur Bordmittel&raquo;) oder eine Collection aus der{' '}
              <strong>Ansible Galaxy</strong> braucht. Ist eine genannt, muss sie auf
              dem Wartungs-PC liegen:{' '}
              <code className="font-mono text-xs">
                ansible-galaxy collection install &lt;name&gt;
              </code>{' '}
              &mdash; sonst scheitert der Lauf am ersten Task mit
              &laquo;couldn&rsquo;t resolve module/action&raquo;. Der Client prüft das
              vor dem Start und fährt den Job dann gar nicht erst. Auch dieser Wert
              steht im Meta-Header, Zeile{' '}
              <code className="font-mono text-xs">
                required_collections: [ansible.windows]
              </code>; ohne Angabe gilt &laquo;nur Bordmittel&raquo;.
            </p>
          </Card>

          <PlaybookPanel slug={slug} routine={query.data} />
        </>
      )}
    </div>
  );
}

/**
 * Herkunft-Zeile der Metadaten. Wie bei der Reifegrad-Zeile bekommt hier auch
 * der Normalfall Text: eine leere Zelle läse sich nicht von "Daten fehlen"
 * unterscheiden.
 */
function CollectionsValue({
  routine,
}: {
  routine: { required_collections?: string[] | null };
}): JSX.Element {
  const namen = normalizeCollections(routine.required_collections);
  if (namen.length === 0) {
    return (
      <span title={BORDMITTEL_MEANING} data-testid="routine-detail-collections">
        {BORDMITTEL_LABEL}
      </span>
    );
  }
  return (
    <span
      className="font-mono text-xs"
      title={collectionsMeaning(routine.required_collections)}
      data-testid="routine-detail-collections"
    >
      {namen.join(', ')}
    </span>
  );
}

function DefRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : 'text-sm'}>{value}</dd>
    </div>
  );
}
